/**
 * R2 — Alert rules. Declarative chip builder. Per CD spec:
 *   - 6 starter rules out-of-box; operator can toggle on/off + add custom.
 *   - Each rule carries a `kind: 'real' | 'quality'` discriminator so
 *     the operator knows whether the rule fires on world events (act on
 *     the event) or data-pipeline state (check the feed). DATA-QUALITY
 *     rules are tagged inline.
 *   - "~N× would have fired in 7 days" live backtest as the operator
 *     edits — same number that drives the per-rule firedLastWk column.
 *   - Warning if too many rules are active (>10) — alert fatigue rail.
 */

import { useMemo, useState } from 'react';
import type { AlertRule, AlertRuleCondition } from './types';

export interface AlertRulesProps {
  rules: readonly AlertRule[];
}

export function AlertRules({ rules: initialRules }: AlertRulesProps) {
  const [rules, setRules] = useState<readonly AlertRule[]>(initialRules);
  const [building, setBuilding] = useState(false);
  const activeCount = rules.filter(r => r.on).length;

  const toggle = (id: string) => setRules(rules.map(r => r.id === id ? { ...r, on: !r.on } : r));

  return (
    <div>
      <header style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 680 }}>Alert rules</h2>
          <div style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, color: 'var(--bm-text-subtle)', marginTop: 3 }}>
            {activeCount} active · fires into Inbox
            {activeCount > 10 && (
              <span style={{ color: 'var(--bm-threat-3)', marginLeft: 6 }}>
                · ⚠ many active rules may create alert fatigue
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setBuilding(b => !b)}
          style={PRIMARY_BTN_STYLE}
        >{building ? 'Close builder' : '+ New rule'}</button>
      </header>

      {building && <RuleBuilder onClose={() => setBuilding(false)} />}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: building ? 16 : 0 }}>
        {rules.map(r => (
          <div
            key={r.id}
            style={{
              display: 'flex', gap: 12, padding: '10px 12px',
              background: r.on ? 'var(--bm-bg-subtle)' : 'transparent',
              border: '1px solid var(--bm-border)',
              borderRadius: 'var(--bm-radius-sm)',
              opacity: r.on ? 1 : 0.7,
            }}
          >
            <Switch on={r.on} label={r.name} onToggle={() => toggle(r.id)} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {r.name}
                {r.starter && <SmallTag label="starter" />}
                {r.kind === 'quality' && <SmallTag label="data-quality" tone="var(--bm-chg-degraded)" />}
              </div>
              <ConditionsRow when={r.when} notify={r.notify} />
            </div>
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
              justifyContent: 'center', minWidth: 80,
            }} title="How often this rule would have fired in the last 7 days">
              <span style={{
                fontFamily: 'var(--bm-font-mono)', fontSize: 18, fontWeight: 700,
                color: r.firedLastWk > 0 ? 'var(--bm-text)' : 'var(--bm-text-subtle)',
              }}>{r.firedLastWk}</span>
              <span style={{
                fontFamily: 'var(--bm-font-mono)', fontSize: 10, color: 'var(--bm-text-subtle)',
              }}>fired / 7d</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Switch({ on, label, onToggle }: { on: boolean; label: string; onToggle: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={`toggle ${label}`}
      onClick={onToggle}
      style={{
        position: 'relative',
        width: 32, height: 18, borderRadius: 999, padding: 0,
        background: on ? 'var(--bm-accent, #6ea3ff)' : 'var(--bm-bg-strong)',
        border: '1px solid var(--bm-border)',
        cursor: 'pointer',
        flexShrink: 0,
        marginTop: 4,
      }}
    >
      <span style={{
        position: 'absolute', top: 1, left: on ? 14 : 1,
        width: 14, height: 14, borderRadius: 999,
        background: 'var(--bm-bg)',
        transition: 'left 120ms ease',
      }} />
    </button>
  );
}

function SmallTag({ label, tone }: { label: string; tone?: string }) {
  return (
    <span style={{
      fontFamily: 'var(--bm-font-mono)', fontSize: 9.5, fontWeight: 600, letterSpacing: '.3px',
      textTransform: 'uppercase',
      padding: '1px 5px', borderRadius: 3,
      color: tone ?? 'var(--bm-text-subtle)',
      border: `1px solid ${tone ?? 'var(--bm-text-subtle)'}55`,
      background: 'var(--bm-bg-subtle)',
    }}>{label}</span>
  );
}

function ConditionsRow({ when, notify }: { when: readonly AlertRuleCondition[]; notify: readonly string[] }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontFamily: 'var(--bm-font-mono)', fontSize: 11 }}>
      <KeyWord text="WHEN" />
      {when.map((w, i) => (
        <span key={i} style={CONDITION_PILL_STYLE}>
          {w.f} <b>{w.op}</b> {w.v}
        </span>
      ))}
      <KeyWord text="THEN" />
      {notify.map(n => (
        <span key={n} style={{
          padding: '1px 7px', borderRadius: 999,
          fontWeight: 600, color: 'var(--bm-accent, #6ea3ff)',
          border: '1px solid var(--bm-accent, #6ea3ff)55',
          background: 'var(--bm-bg-subtle)',
        }}>{n}</span>
      ))}
    </div>
  );
}

function KeyWord({ text }: { text: string }) {
  return (
    <span style={{
      fontWeight: 700, color: 'var(--bm-text-muted)', letterSpacing: '.5px',
    }}>{text}</span>
  );
}

const CONDITION_PILL_STYLE = {
  padding: '1px 7px', borderRadius: 4,
  color: 'var(--bm-text-muted)',
  background: 'var(--bm-bg-subtle)',
  border: '1px solid var(--bm-border)',
};

const PRIMARY_BTN_STYLE = {
  padding: '6px 12px', borderRadius: 5, cursor: 'pointer',
  fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, fontWeight: 600,
  background: 'var(--bm-accent, #6ea3ff)',
  color: 'var(--bm-bg)',
  border: 'none',
};

function RuleBuilder({ onClose }: { onClose: () => void }) {
  const [conds, setConds] = useState<AlertRuleCondition[]>([{ f: 'tier', op: '≥', v: 'HIGH' }]);
  const [notify, setNotify] = useState<string[]>(['inbox']);
  const fields = ['tier', 'pathogen', 'family', 'region', 'source.state', 'coverage.asymmetry', 'variant.share'];
  const addCond = () => setConds([...conds, { f: 'pathogen', op: 'in', v: 'mpox, marburg' }]);
  // Illustrative backtest — exposes the SHAPE of "would-have-fired" not real values.
  const wouldFire = useMemo(
    () => 3 + conds.length * 2 + (conds.some(c => c.f === 'tier') ? 6 : 0),
    [conds],
  );
  const isQuality = conds.some(c => c.f === 'source.state');

  return (
    <div style={{
      padding: 14, marginTop: 4,
      background: 'var(--bm-bg-subtle)',
      border: '1px solid var(--bm-border)', borderRadius: 'var(--bm-radius-md)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        <KeyWord text="WHEN" />
        {conds.map((c, i) => (
          <span key={i} style={{ ...CONDITION_PILL_STYLE, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <select
              value={c.f}
              onChange={e => setConds(conds.map((x, j) => j === i ? { ...x, f: e.target.value } : x))}
              style={INPUT_STYLE}
            >
              {fields.map(f => <option key={f}>{f}</option>)}
            </select>
            <b>{c.op}</b>
            <input
              value={c.v}
              onChange={e => setConds(conds.map((x, j) => j === i ? { ...x, v: e.target.value } : x))}
              size={Math.max(6, c.v.length)}
              style={INPUT_STYLE}
            />
            {conds.length > 1 && (
              <button
                type="button"
                onClick={() => setConds(conds.filter((_, j) => j !== i))}
                style={{ background: 'transparent', border: 'none', color: 'var(--bm-text-subtle)', cursor: 'pointer' }}
                aria-label="remove condition"
              >×</button>
            )}
          </span>
        ))}
        <button type="button" onClick={addCond} style={GHOST_BTN_STYLE}>+ AND</button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        <KeyWord text="THEN notify" />
        {['inbox', 'email', 'push', 'webhook'].map(n => (
          <button
            key={n}
            type="button"
            onClick={() => setNotify(notify.includes(n) ? notify.filter(x => x !== n) : [...notify, n])}
            style={{
              padding: '2px 8px', borderRadius: 999,
              fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 600,
              border: `1px solid ${notify.includes(n) ? 'var(--bm-accent, #6ea3ff)' : 'var(--bm-border)'}`,
              background: notify.includes(n) ? 'var(--bm-bg)' : 'transparent',
              color: notify.includes(n) ? 'var(--bm-text)' : 'var(--bm-text-subtle)',
              cursor: 'pointer',
            }}
          >{n}</button>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, color: 'var(--bm-text-muted)',
        }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--bm-text)' }}>~{wouldFire}×</span>
          {' '}would have fired in the last 7 days
          {isQuality && <span style={{ color: 'var(--bm-chg-degraded)' }}> · data-quality rule (feed health, not outbreak)</span>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={onClose} style={GHOST_BTN_STYLE}>Cancel</button>
          <button type="button" onClick={onClose} style={PRIMARY_BTN_STYLE}>Save rule</button>
        </div>
      </div>
    </div>
  );
}

const INPUT_STYLE = {
  background: 'var(--bm-bg)', color: 'var(--bm-text)',
  border: '1px solid var(--bm-border)', borderRadius: 3,
  padding: '1px 4px', fontFamily: 'var(--bm-font-mono)', fontSize: 11,
};

const GHOST_BTN_STYLE = {
  padding: '4px 10px', borderRadius: 5, cursor: 'pointer',
  fontFamily: 'var(--bm-font-mono)', fontSize: 11, fontWeight: 600,
  background: 'transparent',
  color: 'var(--bm-text-muted)',
  border: '1px solid var(--bm-border)',
};
